// Consumo OBSERVADO e PROJETADO de uma UC, a partir das faturas reais.
//
// Complementa — não substitui — `analiseTecnicaConsumo.ts`. A diferença entre os
// dois é deliberada e não deve ser "unificada":
//
//   analiseTecnicaConsumo.calcularConsumoUc  → grade de 12 meses DIGITADA pelo
//     analista na análise cadastral. Aplica a regra pró-cliente (viés PARA CIMA,
//     `ANC-R30`, decisão travada) porque serve a DIMENSIONAMENTO: subdimensionar
//     deixa o cliente sem crédito no pico. Alimenta público/comissão.
//
//   projecaoConsumo (este módulo)            → faturas REAIS já emitidas. Média
//     SIMPLES, sem viés, porque serve a ALOCAÇÃO DE ENERGIA no rateio: inflar aqui
//     significa mandar para uma UC energia que ela não consome, e `INV-02` exige
//     que o rateio feche exatamente.
//
// Módulo PURO — sem React, sem rede, sem Date. Trabalha em "ordinal de mês"
// (ano*12 + mêsIndex), a mesma convenção de `mesReferenciaParaOrdinal` do rateio,
// para não importar de `views/` (src/lib nunca depende de views).
//
//   node --test src/lib/__tests__/projecaoConsumo.test.ts

import { custoDisponibilidadeDe, type PassoCalculoConsumo } from './analiseTecnicaConsumo.ts';
// A média observada vem de `_shared` — é a MESMA função que a rotina diária usa
// para gravar `consumo_observado_kwh`. Calcular aqui de novo criaria duas versões
// do mesmo número, que concordam hoje e divergem no primeiro ajuste de filtro.
import { mediaObservada } from './consumoObservado.ts';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** 24_312 → 'JAN/2026'. Inverso de mesReferenciaParaOrdinal. */
export function rotuloDeOrdinal(ordinal: number): string {
  const ano = Math.floor(ordinal / 12);
  const mes = ((ordinal % 12) + 12) % 12;
  return `${MESES[mes]}/${ano}`;
}

/** Uma fatura já emitida para a UC. */
export interface ObservacaoMensal {
  /** ano*12 + mêsIndex(0-11). */
  ordinal: number;
  consumoKwh: number;
  /** Energia injetada pela GD do próprio cliente naquele mês, se houver. */
  injecaoKwh?: number | null;
}

export type ConfiancaProjecao = 'alta' | 'media' | 'baixa' | 'sem_dados';

export interface MesProjetado {
  ordinal: number;
  rotulo: string;
  consumoProjetadoKwh: number;
  /** O que a Vigor consegue compensar nesse mês (já sem custo de disponibilidade e sem a GD do cliente). */
  compensavelProjetadoKwh: number;
  /** 'sazonal' usa o mesmo mês do ano anterior; 'media' cai na média observada. */
  metodo: 'sazonal' | 'media';
  /** Só em 'sazonal': o mês que serviu de base, para o analista poder conferir. */
  baseRotulo?: string;
  baseConsumoKwh?: number;
}

export interface ResultadoProjecao {
  /** Média SIMPLES das faturas observadas — sem o viés pró-cliente. */
  consumoObservadoKwh: number;
  mesesObservados: number;
  /** Coeficiente de variação (desvio ÷ média). 0 = consumo perfeitamente estável. */
  dispersao: number;
  confianca: ConfiancaProjecao;
  /** Razão entre os últimos 3 meses e os mesmos 3 meses do ano anterior. 1 = estável. */
  fatorTendencia: number;
  /** true quando o fator foi limitado por `LIMITE_TENDENCIA` — sempre exibir. */
  fatorTendenciaLimitado: boolean;
  custoDisponibilidade: number;
  projecao: MesProjetado[];
  passos: PassoCalculoConsumo[];
  /** Ressalvas que a tela DEVE mostrar. Nunca são silenciadas (INV-01). */
  avisos: string[];
}

/**
 * Um ano de variação year-over-year além disto é quase sempre mudança real de
 * comportamento (ar-condicionado novo, imóvel vago) ou dado ruim — nos dois casos
 * projetar a tendência adiante amplifica o erro. Limita-se e avisa-se.
 */
export const LIMITE_TENDENCIA = { min: 0.7, max: 1.4 } as const;

/** Acima disto o consumo é errático demais para a confiança nominal se sustentar. */
export const DISPERSAO_ALTA = 0.4;

const MESES_PARA_ALTA = 12;
const MESES_PARA_MEDIA = 5;

const arred = (n: number) => Math.round(n);
const kwh = (n: number) => `${arred(n).toLocaleString('pt-BR')} kWh`;

function media(valores: number[]): number {
  if (valores.length === 0) return 0;
  return valores.reduce((a, b) => a + b, 0) / valores.length;
}

/** Coeficiente de variação — 0 quando há menos de 2 pontos ou média zero. */
function coeficienteVariacao(valores: number[]): number {
  if (valores.length < 2) return 0;
  const m = media(valores);
  if (m <= 0) return 0;
  const variancia = media(valores.map((v) => (v - m) ** 2));
  return Math.sqrt(variancia) / m;
}

function classificarConfianca(meses: number, dispersao: number): ConfiancaProjecao {
  if (meses === 0) return 'sem_dados';
  let nivel: ConfiancaProjecao =
    meses >= MESES_PARA_ALTA ? 'alta' : meses >= MESES_PARA_MEDIA ? 'media' : 'baixa';
  // Consumo errático rebaixa um nível: ter 12 meses não ajuda se eles não
  // concordam entre si.
  if (dispersao > DISPERSAO_ALTA) {
    if (nivel === 'alta') nivel = 'media';
    else if (nivel === 'media') nivel = 'baixa';
  }
  return nivel;
}

/**
 * Calcula o consumo observado e projeta os próximos `horizonteMeses` meses.
 *
 * Método da projeção, em ordem de preferência:
 *  1. SAZONAL — usa o mesmo mês do ano anterior, corrigido por um fator de
 *     tendência (últimos 3 meses ÷ mesmos 3 meses do ano anterior). É o que
 *     captura o verão de Goiás, e é auditável linha a linha pelo analista.
 *  2. MÉDIA — sem 12 meses de histórico não há mês-base do ano anterior; cai na
 *     média observada, e a confiança reflete isso.
 *
 * Nunca inventa número: sem observação nenhuma, devolve `sem_dados` e projeção
 * vazia, para o chamador decidir o fallback explicitamente (INV-01).
 */
export function calcularProjecaoConsumo({
  observacoes,
  ordinalAtual,
  horizonteMeses = 3,
  tipoLigacao,
  possuiGD = false,
}: {
  observacoes: ObservacaoMensal[] | null | undefined;
  /** Ordinal do mês de referência — a projeção começa no mês seguinte. */
  ordinalAtual: number;
  horizonteMeses?: number;
  tipoLigacao?: string | null;
  possuiGD?: boolean | null;
}): ResultadoProjecao {
  const custoDisponibilidade = custoDisponibilidadeDe(tipoLigacao);
  const comGD = possuiGD === true;
  const avisos: string[] = [];

  // Só entram meses com consumo positivo; fatura zerada é UC vazia ou erro de
  // leitura, e nos dois casos ela não descreve o consumo do cliente.
  //
  // `Number(...)` explícito porque coluna `numeric` do Postgres chega como STRING
  // pelo supabase-js — `Number.isFinite('1000')` é false e o mês sumiria calado.
  const normalizadas = (Array.isArray(observacoes) ? observacoes : [])
    .map((o) => ({ ...o, consumoKwh: Number(o?.consumoKwh), injecaoKwh: Number(o?.injecaoKwh) || 0 }))
    .filter((o) => Number.isFinite(o.consumoKwh) && o.consumoKwh > 0)
    .sort((a, b) => a.ordinal - b.ordinal);

  // Duas faturas do mesmo mês (reemissão — cenário real, ver `FIN-R09`) contariam
  // duas vezes na soma E na contagem, deslocando a média. O mês fica com a
  // primeira ocorrência, igual ao que a ponte do rateio já faz.
  const validas: ObservacaoMensal[] = [];
  const vistos = new Set<number>();
  for (const o of normalizadas) {
    if (vistos.has(o.ordinal)) continue;
    vistos.add(o.ordinal);
    validas.push(o);
  }

  const vazio: ResultadoProjecao = {
    consumoObservadoKwh: 0,
    mesesObservados: 0,
    dispersao: 0,
    confianca: 'sem_dados',
    fatorTendencia: 1,
    fatorTendenciaLimitado: false,
    custoDisponibilidade,
    projecao: [],
    passos: [
      {
        titulo: 'Sem faturas observadas',
        detalhe:
          'Nenhuma fatura com consumo registrado para esta UC. Sem histórico real não há projeção — ' +
          'use a estimativa da análise cadastral, sabendo que é uma estimativa.',
        valor: '—',
      },
    ],
    avisos: ['UC sem histórico de faturas: nenhuma projeção foi calculada.'],
  };

  if (validas.length === 0) return vazio;

  const consumos = validas.map((o) => o.consumoKwh);
  const consumoObservadoKwh = mediaObservada(validas).kwh;
  const dispersao = coeficienteVariacao(consumos);
  const confianca = classificarConfianca(validas.length, dispersao);

  const porOrdinal = new Map(validas.map((o) => [o.ordinal, o]));

  // ── Fator de tendência: últimos 3 meses vs. os mesmos 3 do ano anterior ──
  const ultimos3 = validas.slice(-3);
  const homologos = ultimos3
    .map((o) => porOrdinal.get(o.ordinal - 12))
    .filter((o): o is ObservacaoMensal => !!o);

  let fatorTendencia = 1;
  let fatorTendenciaLimitado = false;
  const temAnoAnterior = homologos.length === ultimos3.length && homologos.length > 0;

  if (temAnoAnterior) {
    const mediaAtual = media(ultimos3.map((o) => o.consumoKwh));
    const mediaAnterior = media(homologos.map((o) => o.consumoKwh));
    if (mediaAnterior > 0) {
      const bruto = mediaAtual / mediaAnterior;
      fatorTendencia = Math.min(LIMITE_TENDENCIA.max, Math.max(LIMITE_TENDENCIA.min, bruto));
      if (fatorTendencia !== bruto) {
        fatorTendenciaLimitado = true;
        avisos.push(
          `Tendência ano a ano de ${(bruto * 100 - 100).toFixed(0)}% foi limitada a ` +
            `${(fatorTendencia * 100 - 100).toFixed(0)}%. Variação desse tamanho costuma ser mudança ` +
            'real de uso ou dado incorreto — confira o histórico antes de usar a projeção.',
        );
      }
    }
  }

  if (dispersao > DISPERSAO_ALTA) {
    avisos.push(
      `Consumo irregular (variação de ${(dispersao * 100).toFixed(0)}% entre os meses). ` +
        'A projeção é menos confiável para esta UC.',
    );
  }
  if (confianca === 'baixa') {
    avisos.push(
      `Apenas ${validas.length} ${validas.length === 1 ? 'mês observado' : 'meses observados'}: ` +
        'confiança baixa. Trate a projeção como ordem de grandeza, não como número fechado.',
    );
  }

  // ── Projeção mês a mês ──
  const projecao: MesProjetado[] = [];
  for (let i = 1; i <= horizonteMeses; i++) {
    const ordinal = ordinalAtual + i;
    const base = porOrdinal.get(ordinal - 12);

    let consumoProjetadoKwh: number;
    let metodo: 'sazonal' | 'media';
    let baseRotulo: string | undefined;
    let baseConsumoKwh: number | undefined;

    if (base) {
      consumoProjetadoKwh = arred(base.consumoKwh * fatorTendencia);
      metodo = 'sazonal';
      baseRotulo = rotuloDeOrdinal(base.ordinal);
      baseConsumoKwh = arred(base.consumoKwh);
    } else {
      consumoProjetadoKwh = consumoObservadoKwh;
      metodo = 'media';
    }

    // A GD do próprio cliente abate antes de sobrar algo para a Vigor compensar.
    const injecaoBase = comGD && base ? Number(base.injecaoKwh) || 0 : 0;
    const injecaoProjetada = arred(injecaoBase * fatorTendencia);

    projecao.push({
      ordinal,
      rotulo: rotuloDeOrdinal(ordinal),
      consumoProjetadoKwh,
      compensavelProjetadoKwh: Math.max(
        0,
        consumoProjetadoKwh - injecaoProjetada - custoDisponibilidade,
      ),
      metodo,
      baseRotulo,
      baseConsumoKwh,
    });
  }

  const usaramMedia = projecao.filter((p) => p.metodo === 'media').length;
  if (usaramMedia > 0 && validas.length > 0) {
    avisos.push(
      `${usaramMedia} ${usaramMedia === 1 ? 'mês projetado caiu' : 'meses projetados caíram'} na ` +
        'média observada por não haver o mesmo mês do ano anterior no histórico.',
    );
  }

  // ── Explicação ao analista ──
  const passos: PassoCalculoConsumo[] = [
    {
      titulo: '1. Faturas observadas',
      detalhe:
        `${validas.length} ${validas.length === 1 ? 'fatura' : 'faturas'} com consumo registrado, de ` +
        `${rotuloDeOrdinal(validas[0].ordinal)} a ${rotuloDeOrdinal(validas[validas.length - 1].ordinal)}. ` +
        'Faturas zeradas, avulsas e excluídas não entram.',
      valor: `${validas.length} ${validas.length === 1 ? 'mês' : 'meses'}`,
    },
    {
      titulo: '2. Consumo observado',
      detalhe:
        `Média simples do período: ${kwh(consumos.reduce((a, b) => a + b, 0))} ÷ ${validas.length}. ` +
        'Sem a regra pró-cliente da análise cadastral — aqui o número serve para alocar energia, ' +
        'e inflar significaria mandar para esta UC crédito que ela não consome.',
      valor: `${arred(consumoObservadoKwh).toLocaleString('pt-BR')} kWh/mês`,
    },
    {
      titulo: '3. Tendência ano a ano',
      detalhe: temAnoAnterior
        ? `Últimos 3 meses comparados aos mesmos 3 meses do ano anterior: fator ${fatorTendencia.toFixed(2)}` +
          (fatorTendenciaLimitado ? ' (limitado — ver aviso).' : '.')
        : 'Sem 12 meses de histórico não há os mesmos meses do ano anterior para comparar; ' +
          'a projeção segue sem correção de tendência (fator 1,00).',
      valor: `${fatorTendencia.toFixed(2)}×`,
    },
    {
      titulo: '4. Custo de disponibilidade',
      detalhe: `Mínimo faturado pela distribuidora em ligação ${tipoLigacao || 'Monofásico'} ` +
        '(REN 1.000/2021). Nunca é compensável.',
      valor: `${custoDisponibilidade} kWh/mês`,
    },
    {
      titulo: '5. Projeção',
      detalhe:
        'Cada mês usa o mesmo mês do ano anterior corrigido pela tendência; quando esse mês não ' +
        'existe no histórico, cai na média observada. O compensável desconta a GD do cliente e o ' +
        'custo de disponibilidade.',
      valor: projecao.length > 0
        ? `${projecao.map((p) => `${p.rotulo}: ${arred(p.consumoProjetadoKwh).toLocaleString('pt-BR')}`).join(' · ')} kWh`
        : '—',
    },
  ];

  return {
    consumoObservadoKwh,
    mesesObservados: validas.length,
    dispersao,
    confianca,
    fatorTendencia,
    fatorTendenciaLimitado,
    custoDisponibilidade,
    projecao,
    passos,
    avisos,
  };
}
